import { APP_STATUSES, APP_TYPES, VALIDATION_STATUSES } from '@constants/individualPortal/dashboard/dashboardModelTypes';
import { processCommon } from '../../dashboard/processCommon';

const {
    isMultipleChangePlansAllowed,
    isEnrolledInDental,
    isAIAN,
    isPeriodWithinDates,
    isPriorToOEStartDate,
    isChangePlans,
    isSuperUser,
    isRenewalStatusSet,
    isQHPEligible,
    isQHPMedicaidMAGIEligible,
    marketPlaceNotEligible,
    isManualEnrollment,
    isActiveRenewal,
    isTermEnrollee,
    checkFPL
} = processCommon;

const conditions = {
    // INSIDE OE or OUTSIDE OE
    'c_tabIsInsideOE': (sm, { isInsideOEWindow }) => isInsideOEWindow,
    'c_tabIsOutsideOE': (sm, { isInsideOEWindow }) => !isInsideOEWindow,

    'c_tabIsInsideOEEnrollment': (sm, { isInsideOEEnrollmentWindow }) => isInsideOEEnrollmentWindow,
    'c_tabIsOutsideOEEnrollment': (sm, { isInsideOEEnrollmentWindow }) => !isInsideOEEnrollmentWindow,

    //APPLICATION STATUSES
    'c_AppStatusER': (sm, { applicationStatus }) => applicationStatus === APP_STATUSES.ER,
    'c_AppStatusPN': (sm, { applicationStatus }) => applicationStatus === APP_STATUSES.PN,
    'c_AppStatusEN': (sm, { applicationStatus }) => applicationStatus === APP_STATUSES.EN,
    'c_AppStatusOP': (sm, { applicationStatus }) => applicationStatus === APP_STATUSES.OP,
    'c_AppStatusQU': (sm, { applicationStatus }) => applicationStatus === APP_STATUSES.QU,
    'c_AppStatusUC': (sm, { applicationStatus }) => applicationStatus === APP_STATUSES.UC,
    'c_AppStatusEN_PN_ER': (sm, model) => {
        const appStatuses = [APP_STATUSES.EN, APP_STATUSES.PN, APP_STATUSES.ER];
        return appStatuses.includes(model.applicationStatus);
    },
    'c_AppStatusEN_PN': (sm, model) => {
        const appStatuses = [APP_STATUSES.EN, APP_STATUSES.PN];
        return appStatuses.includes(model.applicationStatus);
    },
    'c_AppStatusER_PN': (sm, model) => {
        const appStatuses = [APP_STATUSES.ER, APP_STATUSES.PN];
        return appStatuses.includes(model.applicationStatus);
    },
    'c_AppStatusSG_SU': (sm, model) => {
        const appStatuses = [APP_STATUSES.SG, APP_STATUSES.SU];
        return appStatuses.includes(model.applicationStatus);
    },
    'c_AppStatusOP_UC': (sm, model) => {
        const appStatuses = [APP_STATUSES.OP, APP_STATUSES.UC];
        return appStatuses.includes(model.applicationStatus);
    },
    'c_AppStatusGroup1': (sm, model) => {
        if (!model.applicationStatus) {
            return true;
        }
        const appStatuses = [APP_STATUSES.CL, APP_STATUSES.CC];
        return appStatuses.includes(model.applicationStatus);
    },
    'c_AppStatusGroup2': (sm, model) => {
        if (!model.applicationStatus) {
            return true;
        }
        const appStatuses = [APP_STATUSES.CL, APP_STATUSES.CC];
        return appStatuses.includes(model.applicationStatus);
    },

    // APPLICATION TYPES
    'c_AppTypeQEP': (sm, { applicationType }) => applicationType === APP_TYPES.QEP,
    'c_AppTypeSEP': (sm, { applicationType }) => applicationType === APP_TYPES.SEP,
    'c_AppTypeOEP': (sm, { applicationType }) => applicationType === APP_TYPES.OE,


    // EVENTS
    'c_eventIdentified': (sm, { sepEvent }) => sepEvent.isIdentified,
    'c_eventNotIdentified': (sm, { sepEvent }) => !sepEvent.isIdentified,

    'c_eventVerified': (sm, { sepEvent }) => sepEvent.isVerified,
    'c_eventNotVerified': (sm, { sepEvent }) => !sepEvent.isVerified,

    'c_documentUploaded': (sm, { sepEvent }) => sepEvent.isDocumentUploadedAndVerified,
    'c_notDocumentUploaded': (sm, { sepEvent }) => !sepEvent.isDocumentUploadedAndVerified,

    'c_verificationStatusPending': (sm, { validationStatus }) => validationStatus === VALIDATION_STATUSES.PENDING,
    'c_verificationStatusPendingInfo': (sm, { validationStatus }) => validationStatus === VALIDATION_STATUSES.PENDING_INFO,

    // OTHER
    'c_SEPOpen': (sm, { isSepOpen }) => isSepOpen,
    'c_SEPNotOpen': (sm, { isSepOpen }) => !isSepOpen,

    'c_enrolledInDental': (sm, { enrollments }, config) => isEnrolledInDental(enrollments, config.serverDate),
    'c_notEnrolledInDental': (sm, { enrollments }, config) => !isEnrolledInDental(enrollments, config.serverDate),

    'c_dentalAppStatusEN': (sm, { applicationDentalStatus }) => applicationDentalStatus === APP_STATUSES.EN,
    'c_dentalAppStatusNotEN': (sm, { applicationDentalStatus }) => applicationDentalStatus !== APP_STATUSES.EN,

    'c_dentalAppStatusPN': (sm, { applicationDentalStatus }) => applicationDentalStatus === APP_STATUSES.PN,
    'c_dentalAppStatusNotPN': (sm, { applicationDentalStatus }) => (applicationDentalStatus != APP_STATUSES.PN && applicationDentalStatus != APP_STATUSES.EN),
    'c_dentalAppStatusENorPN': (sm, { applicationDentalStatus }) => applicationDentalStatus === APP_STATUSES.EN || applicationDentalStatus === APP_STATUSES.PN,
    'c_dentalAppStatusNotENorPN': (sm, { applicationDentalStatus }) => applicationDentalStatus !== APP_STATUSES.EN && applicationDentalStatus !== APP_STATUSES.PN,

    'c_dentalAppStatusNull': (sm, { applicationDentalStatus }) => applicationDentalStatus === null,
    'c_dentalAppStatusNotNull': (sm, { applicationDentalStatus }) => applicationDentalStatus !== null,

    'c_appStatusEN': (sm, { applicationStatus, applicationDentalStatus }) => applicationStatus ===  APP_STATUSES.EN && (applicationDentalStatus === APP_STATUSES.EN || applicationDentalStatus === APP_STATUSES.PN),
    'c_appStatusNotEN': (sm, { applicationStatus, applicationDentalStatus }) => applicationStatus !==  APP_STATUSES.EN || (applicationDentalStatus !== APP_STATUSES.EN && applicationDentalStatus !== APP_STATUSES.PN),

    'c_AIAN': (sm, { applicants }) => isAIAN(applicants),
    'c_notAIAN': (sm, { applicants }) => !isAIAN(applicants),

    'c_ShoppingOpenForEntireShoppingWindow': (sm, { applicationType, sepStartDate, sepEndDate, isSepOpen } , config) => isMultipleChangePlansAllowed({ applicationType, sepStartDate, sepEndDate, isSepOpen, config } ),
    'c_notShoppingOpenForEntireShoppingWindow': (sm, { applicationType, sepStartDate, sepEndDate, isSepOpen }, config) => !isMultipleChangePlansAllowed({ applicationType, sepStartDate, sepEndDate, isSepOpen, config } ),

    'c_isShoppingOpenForEntireShoppingWindowFlag': (sm, model, { isShoppingOpenForEntireShoppingWindow, isSepOpen }) => isShoppingOpenForEntireShoppingWindow || isSepOpen,
    'c_isNotShoppingOpenForEntireShoppingWindowFlag': (sm, model, { isShoppingOpenForEntireShoppingWindow, isSepOpen }) => !(isShoppingOpenForEntireShoppingWindow || isSepOpen),

    'c_changePlans': (sm, { enrollments }, config) => isChangePlans(enrollments, config),
    'c_notChangePlans': (sm, { enrollments }, config) => !isChangePlans(enrollments, config),

    'c_supervisor': (sm, { defaultRoleName }) => isSuperUser(defaultRoleName),
    'c_notSupervisor': (sm, { defaultRoleName }) => !isSuperUser(defaultRoleName),

    'c_financial':  (sm, { isFinancial }) => isFinancial,
    'c_nonFinancial':  (sm, { isFinancial }) => !isFinancial,


    // DATES
    'c_periodWithinDates': (sm, model, config) => isPeriodWithinDates(config),

    'c_periodNotWithinDates': (sm, model, config) => !isPeriodWithinDates(config),
    'c_priorToOEStartDate': (sm, { applicationYear }, config) => isPriorToOEStartDate(applicationYear, config),
    'c_notPriorToOEStartDate': (sm, { applicationYear }, config) => !isPriorToOEStartDate(applicationYear, config),
    'c_termEnrolleeWithinOrPastCoverage': (sm, { enrollments }, config) => isTermEnrollee(enrollments, config.serverDate),
    'c_termEnrolleeNotWithinOrPastCoverage': (sm, { enrollments }, config) => !isTermEnrollee(enrollments, config.serverDate),

    // RENEWALS
    'c_renewalStatusSet': (sm, { healthRenewalStatus, dentalRenewalStatus }) => isRenewalStatusSet(healthRenewalStatus, dentalRenewalStatus),
    'c_renewalStatusNotSet': (sm, { healthRenewalStatus, dentalRenewalStatus }) => !isRenewalStatusSet(healthRenewalStatus, dentalRenewalStatus),
    'c_renewalDentalManual': (sm, { dentalRenewalStatus }) => isManualEnrollment(dentalRenewalStatus),
    'c_renewalDentalNotManual': (sm, { dentalRenewalStatus }) => !isManualEnrollment(dentalRenewalStatus),
    'c_activeRenewal': (sm, { isInsideOEEnrollmentWindow }, config) => isActiveRenewal(isInsideOEEnrollmentWindow, config.oepStartDate, config.serverDate),
    'c_notActiveRenewal': (sm, { isInsideOEEnrollmentWindow }, config) => !isActiveRenewal(isInsideOEEnrollmentWindow, config.oepStartDate, config.serverDate),

    'c_QHPEligible': (sm, { applicants }) => isQHPEligible(applicants),
    'c_QHPNotEligible': (sm, { applicants }) => !isQHPEligible(applicants),

    'c_marketPlaceEligible': (sm, { applicants }) => !marketPlaceNotEligible(applicants),
    'c_marketPlaceNotEligible': (sm, { applicants }) => marketPlaceNotEligible(applicants),

    'c_isLocked': (sm, { isLocked }) => isLocked,
    'c_isNotLocked': (sm, { isLocked }) => !isLocked,
    'c_isUnlimitedShoppingEnabled_isLocked': (sm, {  isLocked }, config)  => config.isShoppingOpenForEntireShoppingWindow && isLocked,
    'c_isUnlimitedShoppingEnabled_isNotLocked': (sm, {  isLocked }, config) => config.isShoppingOpenForEntireShoppingWindow && !isLocked,
    'c_isUnlimitedShoppingNotEnabled_isLocked': (sm, {  isLocked }, config) => !config.isShoppingOpenForEntireShoppingWindow && isLocked,
    'c_isUnlimitedShoppingNotEnabled_isNotLocked': (sm, {  isLocked }, config) => !config.isShoppingOpenForEntireShoppingWindow && !isLocked,
    'c_insideOEWindow_isLocked': (sm, { isInsideOEEnrollmentWindow, isLocked }) => isInsideOEEnrollmentWindow && isLocked,
    'c_insideOEWindow_isNotLocked': (sm, { isInsideOEEnrollmentWindow, isLocked }) => isInsideOEEnrollmentWindow && !isLocked,
    'c_outsideOEWindow_isLocked': (sm, { isInsideOEEnrollmentWindow, isLocked }) => !isInsideOEEnrollmentWindow && isLocked,
    'c_outsideOEWindow_isNotLocked': (sm, { isInsideOEEnrollmentWindow, isLocked }) => !isInsideOEEnrollmentWindow && !isLocked,

    'c_QHPMedicaidMAGIEligible': (sm, { applicants }) => isQHPMedicaidMAGIEligible(applicants),
    'c_notQHPMedicaidMAGIEligible': (sm, { applicants }) => !isQHPMedicaidMAGIEligible(applicants),

    'c_lowFPL': (sm, { fpl, applicants }, { lowFplOnceAMonthShoppingThreshold, flags: { lowFplToEnableOnceAMonthShopping } }) => checkFPL(fpl, applicants, lowFplToEnableOnceAMonthShopping, lowFplOnceAMonthShoppingThreshold),
    'c_notLowFPL': (sm, { fpl, applicants }, { lowFplOnceAMonthShoppingThreshold, flags: { lowFplToEnableOnceAMonthShopping } }) => !checkFPL(fpl, applicants, lowFplToEnableOnceAMonthShopping, lowFplOnceAMonthShoppingThreshold),
};

export default conditions;